import type { ReactNode } from 'react';
import { formatAbsolute, formatRelative } from '@odo/shared-ui';

export const CARD_LABEL_CLASS = 'text-[11px] font-bold uppercase tracking-wider text-gray-500';
export const CARD_TEXT_CLASS = 'text-sm text-gray-700 leading-relaxed';
export const PANEL_BODY_CLASS = 'flex-1 min-h-0 overflow-y-auto scrollbar-hide px-5 py-4';
export const HEADER_BUTTON_CLASS =
    'inline-flex items-center gap-1 px-2 py-[3px] rounded-md border border-gray-300 text-[11px] font-medium text-gray-500 bg-white hover:border-gray-400 hover:text-gray-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0';

interface PanelHeaderProps {
    title: string;
    meta?: ReactNode;
    children?: ReactNode;
}

export function PanelHeader({ title, meta, children }: PanelHeaderProps) {
    return (
        <div className="px-5 h-12 border-b border-gray-100 flex items-center justify-between flex-shrink-0 gap-2">
            <div className="flex items-center gap-2 min-w-0">
                <span className="font-bold text-sm text-gray-800 truncate">{title}</span>
                {meta && (
                    <span className="text-[11px] text-gray-500 whitespace-nowrap">{meta}</span>
                )}
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">{children}</div>
        </div>
    );
}

interface VersionCardProps {
    label: string;
    badge?: ReactNode;
    author?: string;
    at?: string;
    muted?: boolean;
    attention?: boolean;
    action?: ReactNode;
    children?: ReactNode;
}

function frameTone(muted: boolean, attention: boolean): string {
    if (attention) return 'border-yellow-200 bg-yellow-50/40';
    return muted ? 'border-gray-200 bg-gray-50/40' : 'border-gray-300 bg-white';
}

export function VersionCard({
    label,
    badge,
    author,
    at,
    muted = false,
    attention = false,
    action,
    children
}: VersionCardProps) {
    const headerTone = attention ? 'bg-yellow-50 border-yellow-200' : 'border-gray-100';
    return (
        <div className={`rounded-lg border ${frameTone(muted, attention)} overflow-hidden`}>
            <div
                className={`px-4 py-2.5 flex items-center justify-between gap-3 ${children ? 'border-b' : ''} ${headerTone} flex-wrap`}
            >
                <div className="flex items-center gap-2 min-w-0">
                    <span className={`${CARD_LABEL_CLASS} flex-shrink-0`}>{label}</span>
                    {badge}
                </div>
                <div className="ml-auto flex items-center gap-2 min-w-0">
                    {author && at && (
                        <div className="text-[11px] text-gray-500 flex items-center gap-2 min-w-0">
                            <span className="truncate">{author}</span>
                            <span className="text-gray-300">·</span>
                            <span title={formatAbsolute(at)} className="whitespace-nowrap">
                                {formatRelative(at) || formatAbsolute(at)}
                            </span>
                        </div>
                    )}
                    {action}
                </div>
            </div>
            {children && <div className="px-4 py-3">{children}</div>}
        </div>
    );
}
